import axios from 'axios'

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api'

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Add response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const refreshToken = localStorage.getItem('refreshToken')
        if (refreshToken) {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refreshToken,
          })
          
          const { accessToken, refreshToken: newRefreshToken } = response.data
          localStorage.setItem('accessToken', accessToken)
          localStorage.setItem('refreshToken', newRefreshToken)
          
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return apiClient(originalRequest)
        }
      } catch (refreshError) {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('refreshToken')
        window.location.href = '/login'
      }
    }

    return Promise.reject(error)
  }
)

export interface Notification {
  id: number;
  user_id: number;
  type: string;
  title: string;
  message: string;
  category: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  related_entity_type?: string;
  related_entity_id?: number;
  action_url?: string;
  action_label?: string;
  is_read: boolean;
  email_sent: boolean;
  email_sent_at?: string;
  read_at?: string;
  expires_at?: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationQuery {
  page?: number;
  limit?: number;
  filter?: 'all' | 'read' | 'unread';
  category?: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
}

export interface NotificationResponse {
  notifications: Notification[];
  pagination: {
    total: number;
    pages: number;
    current: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  unreadCount: number;
}

export interface NotificationCount {
  total: number;
  unread: number;
  byCategory: Record<string, number>;
  byPriority: Record<string, number>;
}

export interface NotificationPreferences {
  categories: Record<string, {
    emailEnabled: boolean;
    inAppEnabled: boolean;
  }>;
}

export interface BulkActionRequest {
  notificationIds: number[];
  action: 'mark_read' | 'mark_unread' | 'delete';
}

export interface UpdatePreferencesRequest {
  preferences: Record<string, {
    emailEnabled: boolean;
    inAppEnabled: boolean;
  }>;
}

export class NotificationService {
  /**
   * Get notifications for the current user
   */
  static async getNotifications(query?: NotificationQuery): Promise<NotificationResponse> {
    const params = new URLSearchParams();
    
    if (query?.page) params.append('page', query.page.toString());
    if (query?.limit) params.append('limit', query.limit.toString());
    if (query?.filter) params.append('filter', query.filter);
    if (query?.category) params.append('category', query.category);
    if (query?.priority) params.append('priority', query.priority);

    const response = await apiClient.get(`/notifications?${params.toString()}`);
    return response.data;
  }

  /**
   * Get notification by ID
   */
  static async getNotification(id: number): Promise<Notification> {
    const response = await apiClient.get(`/notifications/${id}`);
    return response.data;
  }

  /**
   * Mark notification as read/unread
   */
  static async markAsRead(id: number, isRead: boolean = true): Promise<{ success: boolean; notification: Notification }> {
    const response = await apiClient.put(`/notifications/${id}/read`, { isRead });
    return response.data;
  }

  /**
   * Mark all notifications as read
   */
  static async markAllAsRead(): Promise<{ success: boolean; affectedCount: number }> {
    const response = await apiClient.post('/notifications/mark-all-read');
    return response.data;
  }

  /**
   * Delete notification
   */
  static async deleteNotification(id: number): Promise<{ success: boolean }> {
    const response = await apiClient.delete(`/notifications/${id}`);
    return response.data;
  }

  /**
   * Get notification count
   */
  static async getNotificationCount(): Promise<NotificationCount> {
    const response = await apiClient.get('/notifications/count');
    return response.data;
  }

  /**
   * Perform bulk actions on notifications
   */
  static async bulkActions(request: BulkActionRequest): Promise<{ success: boolean; affectedCount: number }> {
    const response = await apiClient.post('/notifications/bulk-actions', request);
    return response.data;
  }

  /**
   * Get notification preferences
   */
  static async getPreferences(): Promise<NotificationPreferences> {
    const response = await apiClient.get('/notifications/preferences');
    return response.data;
  }

  /**
   * Update notification preferences
   */
  static async updatePreferences(request: UpdatePreferencesRequest): Promise<{ success: boolean }> {
    const response = await apiClient.put('/notifications/preferences', request);
    return response.data;
  }

  /**
   * Send notification (admin/manager only)
   */
  static async sendNotification(data: {
    userId: number;
    type: string;
    title: string;
    message: string;
    category: string;
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    relatedEntityType?: string;
    relatedEntityId?: number;
    actionUrl?: string;
    actionLabel?: string;
  }): Promise<{ success: boolean; notification: Notification }> {
    const response = await apiClient.post('/notifications/send', data);
    return response.data;
  }

  /**
   * Send bulk notifications (admin/manager only)
   */
  static async sendBulkNotifications(notifications: Array<{
    userId: number;
    type: string;
    title: string;
    message: string;
    category: string;
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    relatedEntityType?: string;
    relatedEntityId?: number;
    actionUrl?: string;
    actionLabel?: string;
  }>): Promise<{
    success: boolean;
    results: Array<{ userId: number; notification: Notification }>;
    errors: Array<{ userId: number; error: string }>;
    summary: {
      total: number;
      successful: number;
      failed: number;
    };
  }> {
    const response = await apiClient.post('/notifications/send-bulk', { notifications });
    return response.data;
  }
} 